import type { MessageType, IMessage } from 'magpie-types';
import * as t from 'ts-schema';
import type { Exact } from '../types';
import { MessageBuilder } from './message-builder';
import { Subject } from 'reactive-lib/observable/subject';

export interface MessageSpec {
  type: MessageType;
  payloadSchema?: t.Schema<any>;
  reply?: MessageDefinition<MessageSpec>;
}

export interface IMessageDefinition {
  spec: MessageSpec;
}

export type MessageDefinitions = ReadonlyArray<IMessageDefinition>;

type GetPayloadType<MSpec extends MessageSpec> =
  MSpec['payloadSchema'] extends t.Schema<any>
    ? t.SchemaType<MSpec['payloadSchema']>
    : never;

export type GetReplySpec<MSpec extends MessageSpec> =
  MSpec['reply'] extends MessageDefinition<infer ReplySpec> ? ReplySpec : never;

export interface Message<MSpec extends MessageSpec> extends IMessage {
  type: MSpec['type'];
  payload: GetPayloadType<MSpec>;
}

export type GetMessageSpec<M> = M extends Message<infer MSpec> ? MSpec : never;

/**
 * A convenience type for getting the message type from a message definition.
 * This allows specifying subscriber types when they're not inlined in the
 * `topic.on` call.
 */
export type MessageOf<Def extends IMessageDefinition> = Message<Def['spec']>;

export type GetMessageType<Def extends IMessageDefinition> =
  Def['spec']['type'];

type CreateFn<MSpec extends MessageSpec> = [GetPayloadType<MSpec>] extends [
  never,
]
  ? () => MessageBuilder<Message<MSpec>>
  : (payload: GetPayloadType<MSpec>) => MessageBuilder<Message<MSpec>>;

export interface MessageDefinition<MSpec extends MessageSpec>
  extends IMessageDefinition {
  spec: MSpec;

  create: CreateFn<MSpec>;
  getType: () => MSpec['type'];
}

export function defineMessage<
  const Type extends string,
  const MSpec extends MessageSpec,
>(spec: Exact<MSpec & { type: Type }, MessageSpec>): MessageDefinition<MSpec> {
  const commonProps = {
    type: spec.type,
    replyType: spec.reply?.getType(),
  };
  return {
    spec,
    create: (typeof spec.payloadSchema === 'undefined'
      ? () => new MessageBuilder({ ...commonProps, payloadSchema: undefined })
      : (payload: GetPayloadType<MSpec>) =>
          new MessageBuilder({
            ...commonProps,
            payload,
            payloadSchema: spec.payloadSchema,
          })) as CreateFn<MSpec>,
    getType: () => spec.type,
  };
}

export type AnyMessageSubscriber = (msg: IMessage) => void;

export type MessageLink = Subject<IMessage>;
